import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, useLocation, Navigate, useNavigate } from 'react-router-dom';
import HamburgerMenu from './components/HamburgerMenu';
import Home from './pages/Home';
import Login from './pages/Login';
import EventAnalysisTest from './pages/EventAnalysisTest';
import Races from './pages/Races';
import EventTest from './pages/EventTest';
import EventHighlights from './pages/EventHighlights';
import Courses from './pages/Courses';
import CourseTest from './pages/CourseTest';
import Clubs from './pages/Clubs';
import Athletes from './pages/Athletes';
import NextEvent from './pages/NextEvent';
import Lists from './pages/Lists';
import Feedback from './pages/Feedback';
import Chat from './pages/Chat';
import Admin from './pages/Admin';
import QuickStart from './pages/QuickStart';
import NavigationStackOverlay from './components/NavigationStackOverlay';
import QuickStartTourOverlay from './components/QuickStartTourOverlay';
import EventHelpManual, { UnifiedHelpOverlay, UNIFIED_HELP_EVENT, getPageMarkerForPath, readQuickStartHelpRequest, readQuickStartTourRequest, stripQuickStartHelpRequest, stripQuickStartTourRequest, type UnifiedHelpAnchor, type UnifiedHelpRequestDetail } from './pages/UnifiedHelp';
import { API_BASE_URL } from './api/backendAPI';
import { useColumnHeaderMode } from './utils/useColumnHeaderMode';
import { useDelayedUnifiedHelp } from './utils/useDelayedUnifiedHelp';
import './styles/main.css';

/*
    Routing note:
    - Preferred active pages: `EventAnalysisTest`, `EventTest`, `CourseTest`.
    - Legacy pages (`Results`, `Races`, `Courses`) remain routed to support existing links.
    - Before archiving legacy routes, verify all inbound references/navigation paths are migrated.
*/

const AUTH_TOKEN_KEY = 'auth_token_v1';

const headings: { [key: string]: string } = {
    '/': 'Home',
    '/quick-start': 'Quick Start',
    '/login': 'Login',
    '/results': 'Event Analysis Old',
    '/results_test': 'Event Analysis',
    '/event-analysis': 'Event Analysis',
    '/races': 'Event',
    '/event-highlights': 'Event Highlights',
    '/event_test': 'Event',
    '/event_old': 'Event_old',
    '/courses': 'Course Old',
    '/courses_test': 'Course',
    '/course': 'Course',
    '/clubs': 'Club',
    '/athletes': 'Participant',
    '/next-event': 'Next Event',
    '/lists': 'Lists',
    '/chat': 'Chat',
    '/feedback': 'Error / Suggestion Log',
    '/admin': 'Admin',
    '/help-manual': 'Help Manual'
};

const supportsColumnHeaderMode = (path: string): boolean => {
    return path === '/races'
        || path === '/event_test'
        || path === '/event-analysis'
        || path === '/athletes'
        || path === '/next-event'
        || path === '/courses_test'
        || path === '/course'
        || path === '/clubs'
        || path === '/lists';
};

const getTopBarUserLabel = (): string => {
    try {
        const raw = localStorage.getItem('auth_user_v1');
        if (!raw) return '';
        const parsed = JSON.parse(raw) || {};
        const displayName = String(parsed.displayName || parsed.email || '').trim();
        const athleteCode = String(parsed.athleteCode || '').trim();
        if (!displayName && !athleteCode) return '';
        if (displayName && athleteCode) return `Logged in as: ${displayName} [${athleteCode}]`;
        return `Logged in as: ${displayName || athleteCode}`;
    } catch (_err) {
        return '';
    }
};

const TopBar: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const heading = headings[location.pathname] || '';
    const userLabel = getTopBarUserLabel();
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [helpMarkerId, setHelpMarkerId] = useState<string>('top');
    const [helpQuery, setHelpQuery] = useState<string>('');
    const [helpAnchor, setHelpAnchor] = useState<UnifiedHelpAnchor | null>(null);
    const [queuedQuickStartTourTileId, setQueuedQuickStartTourTileId] = useState<string | null>(null);
    const [activeQuickStartTourTileId, setActiveQuickStartTourTileId] = useState<string | null>(null);
    const { mode: columnHeaderMode, toggleMode: toggleColumnHeaderMode } = useColumnHeaderMode();
    const pageMarker = getPageMarkerForPath(location.pathname);
    const showColumnModeToggle = supportsColumnHeaderMode(location.pathname);
    const isColumnHelpMode = columnHeaderMode === 'help';
    const columnModeTooltip = isColumnHelpMode
        ? 'click column for help - click this icon to change to column sorting when column header clicked'
        : 'click column header for sorting - click this icon to change to column help when column header clicked';
    const delayedColumnModeHelp = useDelayedUnifiedHelp(showColumnModeToggle, 2000);

    useEffect(() => {
        const onHelpRequest = (event: Event) => {
            const customEvent = event as CustomEvent<UnifiedHelpRequestDetail>;
            const markerId = String(customEvent?.detail?.markerId || 'top');
            const anchor = customEvent?.detail?.anchor || null;
            const query = String(customEvent?.detail?.query || '');
            setHelpMarkerId(markerId);
            setHelpQuery(query);
            setHelpAnchor(anchor);
            setIsHelpOpen(true);
        };

        window.addEventListener(UNIFIED_HELP_EVENT, onHelpRequest as EventListener);
        return () => window.removeEventListener(UNIFIED_HELP_EVENT, onHelpRequest as EventListener);
    }, []);

    useEffect(() => {
        const quickStartHelpRequest = readQuickStartHelpRequest(location.state);
        if (!quickStartHelpRequest) {
            return;
        }

        const quickStartTourRequest = readQuickStartTourRequest(location.state);
        setQueuedQuickStartTourTileId(quickStartTourRequest?.tileId || null);
        setActiveQuickStartTourTileId(null);

        setHelpMarkerId(quickStartHelpRequest.markerId || pageMarker || 'top');
        setHelpQuery(quickStartHelpRequest.query || '');
        setHelpAnchor(null);
        setIsHelpOpen(true);

        const strippedHelpState = stripQuickStartHelpRequest(location.state);
        const strippedState = stripQuickStartTourRequest(strippedHelpState);

        navigate(
            { pathname: location.pathname, search: location.search },
            {
                replace: true,
                state: strippedState
            }
        );
    }, [location.pathname, location.search, location.state, navigate, pageMarker]);

    const handleHelpClose = () => {
        setIsHelpOpen(false);
        setHelpAnchor(null);

        if (queuedQuickStartTourTileId) {
            setActiveQuickStartTourTileId(queuedQuickStartTourTileId);
            setQueuedQuickStartTourTileId(null);
        }
    };

    return (
        <div className="top-bar" style={{ padding: '0px 0', display: 'flex', alignItems: 'center', position: 'sticky', top: 0, zIndex: 2147483647, background: '#f4f4f4', borderBottom: '1px solid #dbe2ea' }}>
            <HamburgerMenu />
            <div className="top-bar-title-wrap">
                <h1 style={{ marginLeft: '2cm', marginTop: '0.8cm', fontSize: '1.5em' }}>{heading}</h1>
                {pageMarker ? (
                    <div className="top-bar-page-help">
                        <button
                            type="button"
                            className="top-bar-help-btn"
                            aria-label={`${heading} help`}
                            title={`${heading} help`}
                            onClick={(event) => {
                                const rect = (event.currentTarget as HTMLButtonElement).getBoundingClientRect();
                                setHelpMarkerId(pageMarker);
                                setHelpQuery('');
                                setHelpAnchor({
                                    x: rect.left,
                                    y: rect.bottom
                                });
                                setIsHelpOpen(true);
                            }}
                        >
                            📖
                        </button>
                    </div>
                ) : null}
                {showColumnModeToggle ? (
                    <div className="top-bar-column-mode-wrap">
                        <button
                            type="button"
                            className={`top-bar-column-mode-btn ${isColumnHelpMode ? 'help-mode' : 'sort-mode'}`}
                            aria-label={isColumnHelpMode ? 'Column header help mode' : 'Column header sort mode'}
                            title={columnModeTooltip}
                            onMouseEnter={(event) => {
                                delayedColumnModeHelp.schedule({
                                    event,
                                    label: 'Column Sort / Help Icon',
                                    markerId: 'control-column-sort-help',
                                    query: 'Column Sort / Help Icon',
                                    delayMs: 2000
                                });
                            }}
                            onMouseLeave={delayedColumnModeHelp.clear}
                            onMouseDown={delayedColumnModeHelp.clear}
                            onTouchStart={delayedColumnModeHelp.clear}
                            onClick={toggleColumnHeaderMode}
                        >
                            <span className="top-bar-column-mode-text top-bar-column-mode-text-top">column</span>
                            <span className="top-bar-column-mode-icon" aria-hidden="true">{isColumnHelpMode ? '⚑' : '↕'}</span>
                            <span className="top-bar-column-mode-text">{isColumnHelpMode ? 'help' : 'sort'}</span>
                        </button>
                    </div>
                ) : null}
            </div>
            {userLabel && (
                <div
                    style={{
                        position: 'absolute',
                        left: '2cm',
                        marginTop: '1.55cm',
                        fontSize: '0.72rem',
                        color: '#6b7280',
                        fontStyle: 'italic',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                    }}
                    title="Logged in user"
                >
                    {userLabel}
                </div>
            )}

            <UnifiedHelpOverlay
                open={isHelpOpen}
                startMarkerId={helpMarkerId}
                queryTerm={helpQuery}
                anchor={helpAnchor}
                onClose={handleHelpClose}
            />
            <QuickStartTourOverlay
                tileId={activeQuickStartTourTileId}
                open={Boolean(activeQuickStartTourTileId)}
                onClose={() => setActiveQuickStartTourTileId(null)}
            />
        </div>
    );
};

const PageActivityTracker: React.FC = () => {
    const location = useLocation();
    const currentPathRef = useRef(`${location.pathname}${location.search}`);
    const enteredAtRef = useRef<number>(Date.now());

    const shouldSkipAutoTracking = useCallback((path: string): boolean => {
        const normalizedPath = String(path || '').toLowerCase();
        return normalizedPath === '/chat' || normalizedPath.startsWith('/chat?');
    }, []);

    const sendUsage = useCallback((path: string, enteredAt: number, leftAt: number, referrer: string) => {
        if (shouldSkipAutoTracking(path)) {
            return;
        }
        const durationMs = Math.max(0, leftAt - enteredAt);
        const token = localStorage.getItem('auth_token_v1') || undefined;
        const payload = {
            token,
            path,
            enteredAt: new Date(enteredAt).toISOString(),
            leftAt: new Date(leftAt).toISOString(),
            durationMs,
            referrer,
        };

        try {
            fetch(`${API_BASE_URL}/api/analytics/page-visit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                keepalive: true,
            }).catch(() => undefined);
        } catch (_err) {
            // ignore analytics failures
        }
    }, [shouldSkipAutoTracking]);

    useEffect(() => {
        const nextPath = `${location.pathname}${location.search}`;
        const prevPath = currentPathRef.current;
        if (nextPath !== prevPath) {
            const leftAt = Date.now();
            sendUsage(prevPath, enteredAtRef.current, leftAt, nextPath);
            currentPathRef.current = nextPath;
            enteredAtRef.current = leftAt;
        }
    }, [location.pathname, location.search, sendUsage]);

    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'hidden') {
                const leftAt = Date.now();
                sendUsage(currentPathRef.current, enteredAtRef.current, leftAt, document.referrer || 'hidden');
                enteredAtRef.current = leftAt;
            }
        };

        const handleBeforeUnload = () => {
            const leftAt = Date.now();
            sendUsage(currentPathRef.current, enteredAtRef.current, leftAt, 'unload');
            enteredAtRef.current = leftAt;
        };

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [sendUsage]);

    return null;
};

const RootLayout: React.FC = () => (
    <>
        <PageActivityTracker />
        <TopBar />
        <NavigationStackOverlay />
        <div className="app">
            <Outlet />
        </div>
    </>
);

const RequireAuthLayout: React.FC = () => {
    const location = useLocation();
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return <RootLayout />;
};

const LoginOnlyRoute: React.FC = () => {
    return <Login />;
};

// Build router options with a lenient `any` type so we can pass
// future flags that the installed router type definitions may not
// yet include (this silences runtime deprecation warnings).
const _futureOptions: any = { future: { v7_relativeSplatPath: true, v7_startTransition: true } };

const router = createBrowserRouter([
    {
        path: '/',
        element: <RequireAuthLayout />,
        children: [
            { index: true, element: <Home /> },
            { path: 'quick-start', element: <QuickStart /> },
            { path: 'results', element: <Navigate to="/results_test" replace /> },
            { path: 'results_test', element: <EventAnalysisTest /> },
            { path: 'event-analysis', element: <EventAnalysisTest /> },
            { path: 'races', element: <EventTest /> },
            { path: 'event-highlights', element: <EventHighlights /> },
            { path: 'event_test', element: <EventTest /> },
            { path: 'event_old', element: <Races /> },
            { path: 'courses', element: <Courses /> },
            { path: 'courses_test', element: <CourseTest /> },
            { path: 'course', element: <CourseTest /> },
            { path: 'clubs', element: <Clubs /> },
            { path: 'athletes', element: <Athletes /> },
            { path: 'next-event', element: <NextEvent /> },
            { path: 'lists', element: <Lists /> },
            { path: 'chat', element: <Chat /> },
            { path: 'feedback', element: <Feedback /> },
            { path: 'admin', element: <Admin /> },
            { path: 'help-manual', element: <EventHelpManual /> }
        ]
    },
    {
        path: '/login',
        element: <LoginOnlyRoute />
    }
], _futureOptions);

const App: React.FC = () => {
    return (
        <RouterProvider router={router} />
    );
};

export default App;
